<template>
  <coin-dropdown-skeleton v-if="isLoading" />
  <div v-else>
    <base-select
      class="coin-dropdown"
      :model-value="modelValue"
      :label="selectLabel"
      :options="sortedList"
      :placeholder="$t('CoinDropdown | Placeholder')"
      search
      show-selected-item
      :search-placeholder="$t('CoinDropdown | searchPlaceholder')"
      :style="{ color: 'var(--text-3)' }"
      @update:modelValue="onCoinSelected"
      @update:search="search"
      @click.prevent
    >
      <template #trigger>
        <template v-if="activeCoin">
          <div class="flex gap-2">
            <img
              v-if="activeCoin?.coin.media_url && !withoutCoinBalance"
              alt=""
              class="coin-dropdown__main-icon"
              :src="activeCoin?.coin.media_url"
            />
            <div class="coin-dropdown__main">
              <div class="coin-dropdown__main__desc">
                {{ activeCoinBalance }}
                {{ activeCoin?.coin.title.toUpperCase() }}
              </div>
            </div>
          </div>
        </template>
      </template>

      <template #item="{ data }">
        <div class="flex">
          <img
            v-if="!withoutCoinBalance"
            alt=""
            class="coin-dropdown__icon"
            :src="data.coin.media_url"
          />
          <div class="coin-dropdown__title">
            <div class="coin-dropdown__title__name">
              {{ data.coin?.description }}
            </div>
            <div class="coin-dropdown__title__desc">
              {{ withoutCoinBalance ? '' : coinBalance(data.coin_id) }}
              {{ data.coin?.title.toUpperCase() }}
            </div>
          </div>
        </div>
      </template>
      <template #no-data>
        <div class="coin-dropdown__not-found-title">
          {{ $t('CoinDropdown | notFoundTitle') }}
        </div>
        <div class="coin-dropdown__not-found-desc">
          {{ $t('CoinDropdown | notFoundDescription') }}
        </div>
        <div class="coin-dropdown__send-email">
          {{ $t('CoinDropdown | sendToEmail') }}
          <a class="coin-dropdown__email" :href="mailtoLink">support@emcd.io</a>
        </div>
      </template>
    </base-select>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import BaseSelect from '@emcd-shared/base-select/ui'
import CoinDropdownSkeleton from './CoinDropdownSkeleton.vue'
import { formatNumber } from '@/shared/lib/formatNumber'
import { toFixed } from '@/shared/lib/toFixed'

export default {
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
    },
    list: {
      type: Array,
      default: null,
    },
    label: {
      type: Boolean,
      default: true,
    },
    showActiveBalance: {
      type: Boolean,
      default: true,
    },
    withoutCoinBalance: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      searchQuery: '',
    }
  },
  components: {
    BaseSelect,
    CoinDropdownSkeleton,
  },
  computed: {
    ...mapGetters('coinService', ['calculateFiat']),
    ...mapGetters('auth', ['preferredFiat']),
    toFixed,

    activeCoin() {
      return this.modelValue
        ? this.list.find((i) => i.coin_id === this.modelValue)
        : false
    },

    selectLabel() {
      if (this.label) {
        if (this.modelValue) {
          return this.list.find((i) => i.coin_id === this.modelValue)?.coin
            .description
        }
        return ''
      }
      return ''
    },

    activeCoinBalance() {
      const activeCoinBalance = this.list.find(
        (i) => i.coin_id === this.modelValue
      )?.wallet_balance
      return this.showActiveBalance ? formatNumber(activeCoinBalance) : ''
    },

    filteredList() {
      const searchQuery = this.searchQuery.toLowerCase()
      if (searchQuery === '') return this.list

      return this.list.filter((item) => {
        const description = item.coin.description.toLowerCase()
        const title = item.coin.title.toLowerCase()
        return description.includes(searchQuery) || title.includes(searchQuery)
      })
    },

    sortedList() {
      const preferred_fiat = this.preferredFiat.toUpperCase()
      const sortedList = [...this.filteredList].sort((a, b) => {
        const secondCoin = this.calculateFiat(
          b.wallet_balance,
          b.coin_id,
          preferred_fiat
        )
        const firstCoin = this.calculateFiat(
          a.wallet_balance,
          a.coin_id,
          preferred_fiat
        )
        return secondCoin - firstCoin
      })

      // Map over the sorted list to add new properties
      const items = sortedList.map((item) => ({
        ...item,
        text: item.coin.title,
        value: item.coin_id,
        optionValue: item.coin_id,
      }))

      return items
    },

    mailtoLink() {
      const subject = encodeURIComponent(
        this.$t('CoinDropdown | mailtoSubject')
      )
      const body = encodeURIComponent(
        this.$t('CoinDropdown | mailtoBody', [this.searchQuery])
      )
      return `mailto:support@emcd.io?subject=${subject}&body=${body}`
    },
  },

  methods: {
    onCoinSelected(coin) {
      this.$emit('update:modelValue', coin)
    },
    coinBalance(coin) {
      if (this.list.length > 0) {
        const coinBalance = this.list.find(
          (i) => i.coin.id === coin
        )?.wallet_balance
        return formatNumber(coinBalance)
      }
      return ''
    },
    search(query) {
      const keyboardLayout = {
        й: 'q',
        ц: 'w',
        у: 'e',
        к: 'r',
        е: 't',
        н: 'y',
        г: 'u',
        ш: 'i',
        щ: 'o',
        з: 'p',
        х: '[',
        ъ: ']',
        ф: 'a',
        ы: 's',
        в: 'd',
        а: 'f',
        п: 'g',
        р: 'h',
        о: 'j',
        л: 'k',
        д: 'l',
        ж: ';',
        э: "'",
        я: 'z',
        ч: 'x',
        с: 'c',
        м: 'v',
        и: 'b',
        т: 'n',
        ь: 'm',
        б: ',',
        ю: '.',
        '.': '/',
      }

      this.searchQuery = query
        .split('')
        .map((char) => keyboardLayout[char.toLowerCase()] || char)
        .join('')

      return this.searchQuery
    },
  },
}
</script>

<style scoped lang="scss">
.coin-dropdown {
  :deep(button) {
    font-size: 14px;
    color: var(--text-3);

    .base-select--icon {
      color: var(--text-1);
    }
  }

  :deep(.base-select--main) {
    top: 110px !important;
    border-radius: 12px;

    ul {
      .divider-item {
        display: none;
      }
    }
  }

  :deep(.base-select--items) {
    padding: 10px 12px;
  }

  &__main-icon {
    max-width: 24px;
  }

  &__icon {
    max-width: 30px;
    margin-right: 15px;
  }

  &__main {
    display: flex;
    align-items: center;

    &__desc {
      text-align: start;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-1);
    }
  }

  &__placeholder {
    margin: 10px 0px;
    font-size: 12px;
    color: var(--text-3);
    text-align: start;
  }

  &__title {
    &__name {
      font-size: 12px;
      font-weight: 500;
      color: var(--text-3);
      text-align: start;
    }

    &__desc {
      text-align: start;
      font-size: 16px;
      font-weight: 500;
      color: var(--text-1);
    }
  }

  &__check {
    position: absolute;
    width: 20px;
    height: 20px;
    right: 15px;
  }

  &__search {
    position: relative;
    margin: 0 10px;
    margin-bottom: 10px;

    &__input {
      width: 100%;
      padding: 10px 30px 10px 40px;
      border: 1px solid var(--bg-35);
      border-radius: 8px;
      font-size: 14px;
      color: var(--text-3);
      background: var(--bg-35);
      height: 32px;
    }

    &__input::placeholder {
      color: var(--text-3);
      font-size: 12px;
    }

    &__icon-search {
      position: absolute;
      width: 20px;
      height: 20px;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
    }

    &__icon-close {
      z-index: 10;
      position: absolute;
      width: 20px;
      height: 20px;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
    }
  }

  &__not-found-title {
    font-size: 16px;
    font-weight: 500;
    color: var(--text-1);
    text-align: center;
    margin-top: 40px;
  }

  &__not-found-desc {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-3);
    text-align: center;
    margin-top: 5px;
    margin-bottom: 40px;
  }

  &__send-email {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-3);
    text-align: center;
    margin-top: 80px;
    margin-bottom: 25px;
  }

  &__email {
    color: var(--active-link);
  }
}
</style>
